/**
 * @typedef {'dialog' | 'external-link'} DonationPresentation
 *
 * @typedef {Readonly<{
 *   id: string
 *   title: string
 *   description: string | null
 *   donationUrl: string
 *   eventIds: readonly string[]
 *   championshipIds: readonly string[]
 *   presentation: DonationPresentation
 *   autoOpenAfterRegistration: boolean
 * }>} DonationCampaign
 */

const MIND_EVENT_IDS = Object.freeze([
  'a1819c92-038f-4d58-8055-5ef698219218',
  '9b3a62f5-fbbe-4bf3-adc1-445af25f2e35',
  '7ab9243f-092e-4ef9-985b-a4cd26fb5432',
  '46606321-6457-4368-90ee-37d623c75be0',
  'f35de033-cd32-40f9-ab83-3587072a62d0',
  'db9653ff-6cf8-4b3e-959c-81c3b54b5451',
  '48afa952-3b2c-4334-a988-08fba201c5bd',
])

/** @type {readonly DonationCampaign[]} */
export const DONATION_CAMPAIGNS = Object.freeze([
  Object.freeze({
    id: 'mind-week-of-racing-2026',
    title: 'Donate to Mind',
    description: null,
    donationUrl: 'https://www.justgiving.com/page/gridfinder',
    eventIds: MIND_EVENT_IDS,
    championshipIds: Object.freeze([]),
    presentation: 'external-link',
    autoOpenAfterRegistration: false,
  }),
  Object.freeze({
    id: 'imsa-resilient-racers-240',
    title: 'Support the IMSA Resilient Racers 240',
    description:
      'The IMSA Resilient Racers 240 raises funds for the Austin Hatcher Foundation and Camp Boggy Creek. Donate today to support and improve the lives of children and families in need.',
    donationUrl: 'https://www.pledge.to/imsa-resilient-racers-240',
    eventIds: Object.freeze([
      '21cbd241-c416-4a6c-87fa-1cb5e91fa0b2',
      'b79fa1a2-db11-4c55-85c4-b30f798a798f',
    ]),
    championshipIds: Object.freeze([
      '2ff79b3e-37f0-4dcd-b669-2fd9d99e056c',
      'ced8faaf-1a30-4711-9a70-96be3561d122',
    ]),
    presentation: 'dialog',
    autoOpenAfterRegistration: true,
  }),
])

/**
 * Find the donation campaign attached to an event or championship.
 * Event IDs take precedence when both identifiers are provided.
 *
 * @param {{ eventId?: string | null, championshipId?: string | null }} identifiers
 * @returns {DonationCampaign | null}
 */
export function getDonationCampaign({
  eventId = null,
  championshipId = null,
} = {}) {
  if (eventId) {
    const eventCampaign = DONATION_CAMPAIGNS.find((campaign) =>
      campaign.eventIds.includes(eventId),
    )

    if (eventCampaign) return eventCampaign
  }

  if (!championshipId) return null

  return (
    DONATION_CAMPAIGNS.find((campaign) =>
      campaign.championshipIds.includes(championshipId),
    ) || null
  )
}
